import type { TaskPriority, TaskStatus, TaskType } from '@shared/schemas/tasks'
import type { RequestStatus, RequestType } from '@shared/schemas/requests'
import type { FollowUpCategory } from '@shared/schemas/time-entries'

/**
 * Priority and status are both inherently state, not arbitrary categories —
 * so both map onto the same reserved good/warning/critical semantic ramp
 * (plus two neutral steps) rather than an invented rainbow. Shared between
 * task cards and the dashboard so a color always means the same thing.
 */
export const PRIORITY_TAILWIND_BG: Record<TaskPriority, string> = {
  urgent: 'bg-critical',
  high: 'bg-warn',
  medium: 'bg-ink-soft',
  low: 'bg-line',
}

export const PRIORITY_CSS_COLOR: Record<TaskPriority, string> = {
  urgent: 'rgb(var(--color-critical))',
  high: 'rgb(var(--color-warn))',
  medium: 'rgb(var(--color-ink-soft))',
  low: 'rgb(var(--color-line))',
}

export const STATUS_CSS_COLOR: Record<TaskStatus, string> = {
  backlog: 'rgb(var(--color-line))',
  todo: 'rgb(var(--color-ink-soft))',
  in_progress: 'rgb(var(--color-brand))',
  in_review: 'rgb(var(--color-warn))',
  done: 'rgb(var(--color-ok))',
  blocked: 'rgb(var(--color-critical))',
}

export const STATUS_CSS_COLOR_FOR_REQUEST: Record<RequestStatus, string> = {
  new: 'rgb(var(--color-ink-soft))',
  triaged: 'rgb(var(--color-brand))',
  needs_info: 'rgb(var(--color-warn))',
  accepted: 'rgb(var(--color-ok))',
  rejected: 'rgb(var(--color-critical))',
}

/** What kind of request this was — same ramp reused for the Time page's
 * follow-up categories below, since a `department_request` task's
 * category IS its originating request's type. */
export const REQUEST_TYPE_CSS_COLOR: Record<RequestType, string> = {
  new_request: 'rgb(var(--color-ink-soft))',
  bug: 'rgb(var(--color-critical))',
  update: 'rgb(var(--color-warn))',
  enhancement: 'rgb(var(--color-brand))',
  feature: 'rgb(var(--color-ok))',
}

/** Every task's follow-up category on the Time page: `self`/`manager`
 * come from `tasks.source` directly; the request-type values are only
 * used when the source is `department_request` (see docs/DECISIONS.md). */
export const FOLLOWUP_CATEGORY_CSS_COLOR: Record<FollowUpCategory, string> = {
  self: 'rgb(var(--color-line))',
  manager: 'rgb(var(--color-ink-soft))',
  ...REQUEST_TYPE_CSS_COLOR,
}

/** `tasks.type` itself — "is this a bug, a feature, …" independent of
 * who it's for. Used to group the Tasks List view into sections. A
 * request-converted task already gets this from the request at accept
 * time; a self/manager-made task picks it in the New Task form. */
export const TASK_TYPE_CSS_COLOR: Record<TaskType, string> = {
  task: 'rgb(var(--color-ink-soft))',
  bug: 'rgb(var(--color-critical))',
  update: 'rgb(var(--color-warn))',
  enhancement: 'rgb(var(--color-brand))',
  feature: 'rgb(var(--color-ok))',
}
